import IdWithCopy from '@/components/IdWithCopy';
import { JsonViewer } from '@/components/JsonViewer';
import { MetadataItem } from '@/components/MetadataItem';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { processHowOftenResultsState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import type { ProcessHowOftenResultId, TaskType } from '@rock/types';
import { formatCadenceWindow, formatInterval } from '@rock/tasks';
import { ArrowLeft } from 'lucide-react';
import type { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export const ProcessHowOftenResultEntry: FC<{ taskType: TaskType }> = ({ taskType }) => {
    const typeId = taskType.id;
    const { resultId: selectedResultId } = useParams<{ resultId?: ProcessHowOftenResultId }>();
    const [results, resultsLoadable] = useLoadableAtomValue(processHowOftenResultsState(typeId), []);
    const selectedResult = selectedResultId ? results.find((r) => r.id === selectedResultId) : undefined;
    const navigate = useNavigate();

    if (!selectedResult) {
        return (
            <Card>
                <CardContent className="p-6">
                    {resultsLoadable.state === 'loading'
                        ? 'Loading…'
                        : `Process how often result not found (ID: ${selectedResultId}).`}
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader className="border-b-1 pb-3 flex flex-row items-center">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(-1)}
                        aria-label="Back to process how often results"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <CardTitle className="text-xl">Result Details</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                            <MetadataItem
                                label="Result ID"
                                value={<IdWithCopy id={selectedResult.id} variant="mono" />}
                            />
                            <MetadataItem
                                label="Created At"
                                value={new Date(selectedResult.createdAt).toLocaleString()}
                            />
                            <MetadataItem
                                label="Subscription Changed"
                                value={
                                    <span
                                        className={
                                            selectedResult.subscriptionChanged
                                                ? 'text-green-600 font-semibold'
                                                : 'text-gray-600'
                                        }
                                    >
                                        {selectedResult.subscriptionChanged ? 'Yes' : 'No'}
                                    </span>
                                }
                            />
                        </div>
                        <div className="space-y-3">
                            <MetadataItem
                                label="Summary"
                                value={
                                    <div className="whitespace-pre-wrap text-base text-card-foreground">
                                        {selectedResult.result.summary}
                                    </div>
                                }
                            />
                            {selectedResult.result.subscription ? (
                                <MetadataItem
                                    label="Subscription"
                                    value={
                                        <div className="space-y-2">
                                            <div>
                                                <span className="font-medium">Type: </span>
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                    {selectedResult.result.subscription.type}
                                                </span>
                                            </div>
                                            {selectedResult.result.subscription.type === 'recurring' ? (
                                                <div>
                                                    <span className="font-medium">Interval: </span>
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                        {selectedResult.result.subscription.interval.value}{' '}
                                                        {selectedResult.result.subscription.interval.unit}
                                                    </span>
                                                </div>
                                            ) : null}
                                            {selectedResult.result.subscription.offset ? (
                                                <div>
                                                    <span className="font-medium">Offset: </span>
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                                        {formatInterval(selectedResult.result.subscription.offset)}
                                                    </span>
                                                </div>
                                            ) : null}
                                            {selectedResult.result.subscription.window?.length ? (
                                                <div>
                                                    <span className="font-medium">Window: </span>
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                                        {formatCadenceWindow(selectedResult.result.subscription)}
                                                    </span>
                                                </div>
                                            ) : null}
                                        </div>
                                    }
                                />
                            ) : (
                                <MetadataItem
                                    label="Subscription"
                                    value={<span className="text-muted-foreground">None</span>}
                                />
                            )}
                            {selectedResult.result.issues && selectedResult.result.issues.length > 0 ? (
                                <MetadataItem
                                    label="Issues"
                                    value={
                                        <div className="space-y-1">
                                            {selectedResult.result.issues.map((issue, idx) => (
                                                <div
                                                    key={idx}
                                                    className="whitespace-pre-wrap text-base text-destructive"
                                                >
                                                    • {issue}
                                                </div>
                                            ))}
                                        </div>
                                    }
                                />
                            ) : null}
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="border-b-1 pb-3">
                    <CardTitle className="text-xl">AI Context</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        <MetadataItem
                            label="Model"
                            value={<span className="font-mono text-xs">{selectedResult.aiContext.model}</span>}
                        />
                        <MetadataItem
                            label="System Message"
                            value={
                                <pre className="whitespace-pre-wrap text-xs bg-muted p-4 rounded-md overflow-x-auto">
                                    {selectedResult.aiContext.systemMessage}
                                </pre>
                            }
                        />
                        <MetadataItem
                            label="User Message"
                            value={
                                <pre className="whitespace-pre-wrap text-xs bg-muted p-4 rounded-md overflow-x-auto">
                                    {selectedResult.aiContext.userMessage}
                                </pre>
                            }
                        />
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Result Object</CardTitle>
                    <CardDescription>
                        The complete process how often result payload stored in Firestore.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="max-h-[480px] overflow-auto rounded-md bg-muted p-4">
                        <JsonViewer data={selectedResult} />
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};
